import {useMobileAppRedirect} from "@/api/userApi";
import AiSparkIcon from "@/assets/colorable/ai_spark.svg?react";
import PhoneIcon from "@/assets/dark_gradient/phone.svg?react";
import AiSparkGradientIcon from "@/assets/purple_gradient/ai_spark.svg?react";
import Button from "@/components/Common/Button";
import TextButton from "@/components/Common/TextButton";
import InputField from "@/components/Common/forms/InputField";
import {GlowingGradientBorder} from "@/components/ui/GlowingGradientBorder";
import useCountdown from "@/hooks/useCountdown";
import clsxm from "@/lib/clsxm";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {URL_REGEX} from "@/store/constants";
import NumberFlow from "@number-flow/react";
import Rive from "@rive-app/react-canvas";
import {useNavigate} from "@tanstack/react-router";
import {useEffect, useRef, useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useDebounce, useHoverDirty} from "react-use";
import {z} from "zod";

const CRAWLING_LENGTH_SECONDS = 25;
const CLASSIFY_DEBOUNCE_MS = 550;

const SOCIAL_HOSTS = [
  "about.me",
  "beacons.ai",
  "bio.link",
  "campsite.bio",
  "carrd.co",
  "facebook.com",
  "instagram.com",
  "komi.io",
  "linkin.bio",
  "linktr.ee",
  "onlyfans.com",
  "pinterest.com",
  "snapchat.com",
  "substack.com",
  "threads.com",
  "threads.net",
  "tiktok.com",
  "twitch.tv",
  "twitter.com",
  "x.com",
  "youtu.be",
  "youtube.com",
];

const urlSchema = z.string().trim().regex(URL_REGEX, "Invalid URL");

const classifyCompanyUrl = (raw?: string | null) => {
  const value = (raw ?? "").trim().toLowerCase();
  if (!value) return {kind: "empty"} as const;
  if (value.startsWith("@")) return {kind: "blocked", reason: "handle"} as const;

  const host = value
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .split("/")[0];
  const platform = SOCIAL_HOSTS.find(social => host === social || host.endsWith("." + social));

  if (platform) return {kind: "blocked", platform, reason: "platform"} as const;
  return {kind: host.includes(".") ? "brand" : "unclear"} as const;
};

const OnboardingCta: FC<{
  buttonClassName?: string;
  buttonContainerClassName?: string;
  buttonTitle?: string;
  className?: string;
  extraSearchParams?: Record<string, string | number | undefined>;
  inputClassName?: string;
  isLoading?: boolean;
  onClick?: () => void;
  showCreatorAppExit?: boolean;
  value?: string | null;
  setValue: (value: string) => void;
}> = ({
  buttonClassName,
  buttonContainerClassName,
  buttonTitle,
  className,
  extraSearchParams,
  inputClassName,
  isLoading,
  onClick,
  showCreatorAppExit = false,
  value,
  setValue,
}) => {
  const {t, i18n} = useTranslation("common");
  const navigate = useNavigate();
  const {openAppRedirect} = useMobileAppRedirect();
  // Classification is synchronous so that pasting a blocked URL and clicking submit
  // immediately cannot beat the check. Only the explanation panel is debounced, so it
  // does not flash while the user is still typing.
  const classification = classifyCompanyUrl(value);
  const isBlocked = classification.kind === "blocked";
  const isValidUrl = urlSchema.safeParse(value).success && !isBlocked;
  const [isBlockedPanelVisible, setIsBlockedPanelVisible] = useState(false);

  useDebounce(
    () => {
      setIsBlockedPanelVisible(isBlocked);
      if (classification.kind === "blocked") {
        capturePostHogEvent(PostHogEvents.OnboardingUrlBlocked, {
          platform: "platform" in classification ? classification.platform : undefined,
          reason: classification.reason,
        });
      }
    },
    CLASSIFY_DEBOUNCE_MS,
    [value],
  );
  const submitRef = useRef<HTMLDivElement>(null!);
  const isSubmitHovering = useHoverDirty(submitRef);
  const {countdown, reset} = useCountdown({from: CRAWLING_LENGTH_SECONDS, shouldStartCountdown: isLoading});

  useEffect(() => {
    if (!isLoading) {
      reset({shouldStartCountdown: false});
    }
  }, [isLoading]);

  const submitDomain = () =>
    isBlocked
      ? undefined
      : onClick
        ? onClick()
        : navigate({
            to: "/onboarding",
            search: {
              domain: value!,
              language: i18n.resolvedLanguage === "ger" ? "de" : i18n.resolvedLanguage,
              ...extraSearchParams,
            },
          });

  return (
    <div className={clsxm("flex flex-col items-center gap-5", className)}>
      <span className="text-neutral-black text-xl font-semibold">{t("onboarding_registration_cta")}</span>
      <div className="flex items-end gap-1 self-stretch">
        <div className="grow">
          <InputField
            value={value}
            onChange={setValue}
            label={t("onboarding_registration_input_label")}
            placeholder={t("onboarding_registration_input_placeholder")}
            className={clsxm("focus:border-ai-purple pl-10", inputClassName)}
            dataTestId="onboarding-domain-input"
            icon={<AiSparkGradientIcon className="absolute inset-y-0 left-4 my-auto size-4.5 shrink-0" />}
            onKeyDown={e => {
              if (e.key === "Enter") {
                submitDomain();
              }
            }}
          />
        </div>
        <div ref={submitRef} className={clsxm("flex basis-1/2 md:basis-1/3", buttonContainerClassName)}>
          <GlowingGradientBorder containerClassName="w-full" enabled={isValidUrl}>
            {isLoading ? (
              <div
                className={clsxm(
                  "flex h-15 min-w-20 items-center justify-center gap-1 rounded-lg bg-linear-(--brand-medium-gradient) font-semibold",
                  buttonClassName,
                )}>
                <NumberFlow className="text-white" value={countdown} />
              </div>
            ) : (
              <Button
                buttonStyle="Gradient"
                className={clsxm("flex w-full gap-2.5 text-sm sm:text-base", buttonClassName)}
                isDisabled={!isValidUrl}
                dataTestId="onboarding-domain-submit"
                onClick={() => submitDomain()}>
                {buttonTitle ?? t("onboarding_registration_button_text")}
                {isValidUrl &&
                  (isSubmitHovering ? (
                    <Rive src={"/onboarding/stars_sparkle.riv"} className="size-4 shrink-0" />
                  ) : (
                    <AiSparkIcon className="size-4 shrink-0 text-white" />
                  ))}
              </Button>
            )}
          </GlowingGradientBorder>
        </div>
      </div>
      {isBlockedPanelVisible && classification.kind === "blocked" && (
        <div
          className="bg-primary-powder border-primary-light-solid w-full rounded-lg border p-4"
          data-testid="onboarding-domain-blocked">
          <p className="text-neutral-black m-0 text-sm font-bold">{t("onboarding_url_blocked_title")}</p>
          <p className="text-neutral-grey-dark mt-1 mb-0 text-sm">
            {classification.reason === "handle"
              ? t("onboarding_url_blocked_handle")
              : t("onboarding_url_blocked_platform", {platform: classification.platform})}{" "}
            {t("onboarding_url_blocked_description")}
          </p>
          {showCreatorAppExit && (
            <div className="border-primary-light-solid mt-3 flex items-center gap-3 border-t pt-3">
              <PhoneIcon className="h-4 w-auto shrink-0" />
              <span className="text-neutral-grey-dark flex-1 text-left text-xs">
                {t("onboarding_url_blocked_creator_prompt")}
              </span>
              <TextButton
                className="shrink-0 text-xs"
                data-testid="onboarding-domain-blocked-app-link"
                onClick={openAppRedirect}
                text={t("download_the_app")}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OnboardingCta;
